import React, { useEffect, useMemo, useState } from "react";
import { curriculumAuditAPI, lessonAPI, performanceTaskAPI } from "../services/api";
import "./CurriculumAuditPage.css";

const toNumber = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const statusClass = (ratio) => {
  if (ratio >= 0.95) return "audit-status-good";
  if (ratio >= 0.8) return "audit-status-warn";
  return "audit-status-risk";
};

const assert = (condition, message) => {
  if (!condition) throw new Error(message);
};

const getApiErrorMessage = (error, fallback) =>
  error?.response?.data?.error
  || error?.response?.data?.detail
  || error?.message
  || fallback;

const toShuffled = (items = []) => {
  const copy = [...items];
  for (let i = copy.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
};

const summarizeSmokeTask = (task) => ({
  id: task?.id,
  taskKey: task?.taskKey,
  title: task?.title,
  standardsCount: Array.isArray(task?.standards) ? task.standards.length : 0,
  resourcesCount: Array.isArray(task?.resources) ? task.resources.length : 0,
});

const validateLessonPayload = ({ lesson, expectedLessonId, requireData }) => {
  assert(lesson && typeof lesson === "object", "Expected lesson payload object.");
  assert(String(lesson.id || "") === expectedLessonId, "Lesson id mismatch.");
  assert(typeof lesson.lesson_title === "string" && lesson.lesson_title.trim(), "Missing lesson_title.");
  assert(Array.isArray(lesson.assets), "Missing assets array.");
  assert(Array.isArray(lesson.standards), "Missing standards array.");
  assert(Array.isArray(lesson.instructional_supports), "Missing instructional_supports array.");

  if (requireData) {
    assert(lesson.standards.length > 0, "Lesson has empty standards array while requireData=true.");
  }
};

const CurriculumAuditPage = ({ courseId }) => {
  const [overview, setOverview] = useState([]);
  const [overviewLoading, setOverviewLoading] = useState(false);
  const [overviewError, setOverviewError] = useState("");

  const [report, setReport] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportError, setReportError] = useState("");

  const [searchLesson, setSearchLesson] = useState("");
  const [uncoveredOnly, setUncoveredOnly] = useState(false);
  const [pivotMode, setPivotMode] = useState("unit");

  const [smokeSampleSize, setSmokeSampleSize] = useState(8);
  const [smokeRequireMulti, setSmokeRequireMulti] = useState(true);
  const [smokeRequireData, setSmokeRequireData] = useState(true);
  const [smokeRunning, setSmokeRunning] = useState(false);
  const [smokeResult, setSmokeResult] = useState(null);
  const [researchPacketLoading, setResearchPacketLoading] = useState(false);
  const [researchPacketStatus, setResearchPacketStatus] = useState("");

  useEffect(() => {
    let cancelled = false;

    const loadOverview = async () => {
      setOverviewLoading(true);
      setOverviewError("");
      try {
        const response = await curriculumAuditAPI.getOverview();
        if (cancelled) return;
        setOverview(Array.isArray(response.data?.courses) ? response.data.courses : []);
      } catch (error) {
        if (cancelled) return;
        console.error("Error loading curriculum audit overview:", error);
        setOverview([]);
        const serverError =
          error?.response?.data?.error
          || error?.response?.data?.detail
          || error?.message
          || "Unable to load course audit overview.";
        setOverviewError(serverError);
      } finally {
        if (!cancelled) setOverviewLoading(false);
      }
    };

    loadOverview();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadCourseReport = async () => {
      if (!courseId) {
        setReport(null);
        setReportError("");
        return;
      }

      setReportLoading(true);
      setReportError("");
      try {
        const response = await curriculumAuditAPI.getCourse(courseId);
        if (cancelled) return;
        setReport(response.data || null);
      } catch (error) {
        if (cancelled) return;
        console.error("Error loading course audit report:", error);
        setReport(null);
        const serverError =
          error?.response?.data?.error
          || error?.response?.data?.detail
          || error?.message
          || "Unable to load selected course audit report.";
        setReportError(serverError);
      } finally {
        if (!cancelled) setReportLoading(false);
      }
    };

    setSmokeResult(null);
    setResearchPacketStatus("");
    loadCourseReport();
    return () => {
      cancelled = true;
    };
  }, [courseId]);

  const filteredLessons = useMemo(() => {
    const rows = Array.isArray(report?.lessonAudit) ? report.lessonAudit : [];
    const query = String(searchLesson || "").trim().toLowerCase();
    if (!query) return rows;
    return rows.filter((row) => String(row.lesson_title || "").toLowerCase().includes(query));
  }, [report?.lessonAudit, searchLesson]);

  const filteredTeksCoverage = useMemo(() => {
    const rows = Array.isArray(report?.teksCoverage) ? report.teksCoverage : [];
    if (!uncoveredOnly) return rows;
    return rows.filter((row) => !row.is_covered);
  }, [report?.teksCoverage, uncoveredOnly]);

  const unitPivotRows = useMemo(() => {
    const rows = Array.isArray(report?.lessonAudit) ? report.lessonAudit : [];
    const map = new Map();

    rows.forEach((row) => {
      const unitNumber = toNumber(row.unit_number, 0);
      if (!map.has(unitNumber)) {
        map.set(unitNumber, {
          unit_number: unitNumber,
          lessons: 0,
          multi_standard_lessons: 0,
          lessons_missing_eq: 0,
          lessons_missing_objectives: 0,
          avg_standards: 0,
          standards_total: 0,
        });
      }

      const target = map.get(unitNumber);
      target.lessons += 1;
      target.standards_total += toNumber(row.standards_count, 0);
      if (toNumber(row.standards_count, 0) > 1) target.multi_standard_lessons += 1;
      if (!row.has_essential_question) target.lessons_missing_eq += 1;
      if (toNumber(row.calendar_day_count, 0) > 0 && toNumber(row.objective_day_count, 0) === 0) {
        target.lessons_missing_objectives += 1;
      }
    });

    return Array.from(map.values())
      .sort((a, b) => a.unit_number - b.unit_number)
      .map((row) => ({
        ...row,
        avg_standards: row.lessons > 0
          ? Number((row.standards_total / row.lessons).toFixed(2))
          : 0,
      }));
  }, [report?.lessonAudit]);

  const standardsLoadPivotRows = useMemo(() => {
    const rows = Array.isArray(report?.lessonAudit) ? report.lessonAudit : [];
    const buckets = [
      { key: "0", label: "0 standards", count: 0 },
      { key: "1", label: "1 standard", count: 0 },
      { key: "2", label: "2 standards", count: 0 },
      { key: "3-4", label: "3-4 standards", count: 0 },
      { key: "5-8", label: "5-8 standards", count: 0 },
      { key: "9+", label: "9+ standards", count: 0 },
    ];

    rows.forEach((row) => {
      const standardsCount = toNumber(row.standards_count, 0);
      if (standardsCount === 0) buckets[0].count += 1;
      else if (standardsCount === 1) buckets[1].count += 1;
      else if (standardsCount === 2) buckets[2].count += 1;
      else if (standardsCount >= 3 && standardsCount <= 4) buckets[3].count += 1;
      else if (standardsCount >= 5 && standardsCount <= 8) buckets[4].count += 1;
      else buckets[5].count += 1;
    });

    return buckets;
  }, [report?.lessonAudit]);

  const runSmoke = async () => {
    setSmokeRunning(true);
    setSmokeResult(null);

    try {
      const tasksResponse = await performanceTaskAPI.list(courseId || undefined);
      const tasks = Array.isArray(tasksResponse.data) ? tasksResponse.data : [];
      assert(Array.isArray(tasks), "Expected /api/performance-tasks array.");

      const lessonsResponse = await lessonAPI.getAll(courseId ? { courseId } : undefined);
      const lessons = Array.isArray(lessonsResponse.data) ? lessonsResponse.data : [];
      assert(lessons.length > 0, "No lessons available for smoke validation.");

      const sampleSize = Math.max(1, Math.trunc(toNumber(smokeSampleSize, 1)));
      const sampledLessons = toShuffled(lessons).slice(0, Math.min(sampleSize, lessons.length));

      const sampleRows = [];
      for (const lessonRow of sampledLessons) {
        const lessonId = String(lessonRow?.id || "").trim();
        assert(lessonId, "Sampled lesson missing id.");

        const payloadResponse = await lessonAPI.getById(lessonId);
        const lessonPayload = payloadResponse.data;
        validateLessonPayload({
          lesson: lessonPayload,
          expectedLessonId: lessonId,
          requireData: smokeRequireData,
        });

        sampleRows.push({
          id: lessonPayload.id,
          lesson_title: lessonPayload.lesson_title,
          standardsCount: Array.isArray(lessonPayload.standards) ? lessonPayload.standards.length : 0,
          instructionalSupportsCount: Array.isArray(lessonPayload.instructional_supports)
            ? lessonPayload.instructional_supports.length
            : 0,
          assetsCount: Array.isArray(lessonPayload.assets) ? lessonPayload.assets.length : 0,
        });
      }

      const multiStandardLessons = sampleRows.filter((row) => toNumber(row.standardsCount, 0) > 1).length;
      if (smokeRequireMulti) {
        assert(
          multiStandardLessons > 0,
          "Multi-standard requirement enabled, but no sampled lesson had multiple standards.",
        );
      }

      const avgStandardsPerLesson = sampleRows.length
        ? Number(
          (
            sampleRows.reduce((sum, row) => sum + toNumber(row.standardsCount, 0), 0)
            / sampleRows.length
          ).toFixed(2),
        )
        : 0;

      setSmokeResult({
        status: "pass",
        summary: {
          sampled: sampleRows.length,
          taskCount: tasks.length,
          multiStandardLessons,
          lessonsWithInstructionalSupports: sampleRows.filter(
            (row) => toNumber(row.instructionalSupportsCount, 0) > 0,
          ).length,
          avgStandardsPerLesson,
        },
        sampleTask: tasks[0] ? summarizeSmokeTask(tasks[0]) : null,
        sampleRows,
      });
    } catch (error) {
      setSmokeResult({
        status: "fail",
        message: error?.message || "Smoke test failed.",
      });
    } finally {
      setSmokeRunning(false);
    }
  };

  const downloadResearchPacket = async () => {
    if (!courseId) return;

    setResearchPacketLoading(true);
    setResearchPacketStatus("");

    try {
      const response = await curriculumAuditAPI.getResearchPacket(courseId);
      const packet = response.data || {};
      const json = JSON.stringify(packet, null, 2);
      const blob = new Blob([json], { type: "application/json;charset=utf-8" });
      const objectUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      const courseName = String(packet?.course?.name || "course")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "");
      const dateTag = new Date().toISOString().slice(0, 10);

      link.href = objectUrl;
      link.download = `curriculum-gap-research-${courseName || "course"}-${dateTag}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(objectUrl);

      const gapCount = Array.isArray(packet?.gaps) ? packet.gaps.length : 0;
      if (gapCount === 0) {
        setResearchPacketStatus(
          "No uncovered TEKS for this course. Downloaded snapshot with 0 gap rows.",
        );
      } else {
        setResearchPacketStatus(`Downloaded research packet with ${gapCount} uncovered TEKS rows.`);
      }
    } catch (error) {
      console.error("Error downloading curriculum gap research packet:", error);
      setResearchPacketStatus(
        getApiErrorMessage(error, "Could not build research packet right now."),
      );
    } finally {
      setResearchPacketLoading(false);
    }
  };

  return (
    <div className="curriculum-audit-page">
      <section className="panel-card curriculum-audit-intro">
        <h2>Curriculum Audit Workspace</h2>
        <p>
          Review TEKS coverage, scope-to-calendar execution, lesson objective completeness, and
          live API smoke checks from one place.
        </p>
      </section>

      <section className="panel-card curriculum-audit-overview">
        <div className="curriculum-audit-section-header">
          <h3>Course Health Matrix</h3>
          {overviewLoading ? <span>Loading...</span> : null}
          {overviewError ? <span className="audit-error-text">{overviewError}</span> : null}
        </div>

        <div className="audit-table-wrap">
          <table className="audit-table">
            <thead>
              <tr>
                <th>Course</th>
                <th>Units</th>
                <th>Lessons</th>
                <th>TEKS Covered</th>
                <th>Uncovered</th>
                <th>Multi-Std Lessons</th>
                <th>Lessons Missing EQ</th>
                <th>Scope Gaps</th>
              </tr>
            </thead>
            <tbody>
              {overview.map((row) => {
                const covered = toNumber(row.covered_teks_count, 0);
                const total = Math.max(toNumber(row.total_teks_count, 0), 1);
                const coverageRatio = covered / total;
                const isCurrent = String(row.id) === String(courseId || "");

                return (
                  <tr key={row.id} className={isCurrent ? "audit-current-course-row" : ""}>
                    <td>
                      <div className="audit-course-name">{row.name}</div>
                      <div className="audit-grade-tag">Grade {row.grade_level}</div>
                    </td>
                    <td>{row.unit_count}</td>
                    <td>{row.lesson_count}</td>
                    <td>
                      <span className={`audit-status-pill ${statusClass(coverageRatio)}`}>
                        {covered}/{row.total_teks_count}
                      </span>
                    </td>
                    <td>{row.uncovered_teks_count}</td>
                    <td>{row.multi_standard_lessons_count}</td>
                    <td>{row.lessons_without_essential_question_count}</td>
                    <td>{row.scope_windows_without_lessons_count}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      {!courseId ? (
        <section className="panel-card">
          <p>Select a course above to load detailed curriculum audit diagnostics.</p>
        </section>
      ) : null}

      {reportLoading ? (
        <section className="panel-card">
          <p>Loading selected course audit...</p>
        </section>
      ) : null}

      {reportError ? (
        <section className="panel-card">
          <p className="audit-error-text">{reportError}</p>
        </section>
      ) : null}

      {report ? (
        <>
          <section className="panel-card curriculum-audit-summary-grid">
            <article>
              <h4>TEKS Coverage</h4>
              <p>
                {report.summary.teksCoveredCount}/{report.summary.totalTeksCount} covered
              </p>
            </article>
            <article>
              <h4>Uncovered TEKS</h4>
              <p>{report.summary.teksUncoveredCount}</p>
            </article>
            <article>
              <h4>Multi-Standard Lessons</h4>
              <p>{report.summary.multiStandardLessonsCount}</p>
            </article>
            <article>
              <h4>Avg Standards / Lesson</h4>
              <p>{report.summary.averageStandardsPerLesson}</p>
            </article>
            <article>
              <h4>Lesson Days Without Objectives</h4>
              <p>{report.summary.lessonDaysWithoutObjectivesCount}</p>
            </article>
            <article>
              <h4>Scope Windows Missing Lesson Days</h4>
              <p>{report.summary.scopeWindowsWithoutLessonDaysCount}</p>
            </article>
          </section>

          <section className="panel-card curriculum-audit-research">
            <div className="curriculum-audit-section-header">
              <h3>Gap Research Packet</h3>
              <button
                type="button"
                className="primary-btn"
                onClick={downloadResearchPacket}
                disabled={researchPacketLoading || !courseId}
              >
                {researchPacketLoading
                  ? "Building..."
                  : toNumber(report?.summary?.teksUncoveredCount, 0) > 0
                    ? "Download JSON Packet"
                    : "Download Snapshot (No Gaps)"}
              </button>
            </div>
            <p>
              Exports uncovered TEKS with candidate units, related lessons, scoring signals, and a
              strict deep-research output contract.
            </p>
            {toNumber(report?.summary?.teksUncoveredCount, 0) === 0 ? (
              <p className="audit-muted">
                All TEKS are currently covered. A download will include the course snapshot with an
                empty gaps array.
              </p>
            ) : null}
            {researchPacketStatus ? (
              <p
                className={
                  researchPacketStatus.toLowerCase().includes("could not")
                    ? "audit-error-text"
                    : "audit-muted"
                }
              >
                {researchPacketStatus}
              </p>
            ) : null}
          </section>

          <section className="panel-card curriculum-audit-alerts">
            <h3>Priority Findings</h3>
            {Array.isArray(report.alerts) && report.alerts.length > 0 ? (
              <ul className="audit-alert-list">
                {report.alerts.map((alert, index) => (
                  <li key={`${alert.area}-${index}`} className={`audit-alert-${alert.severity}`}>
                    <strong>{alert.area}:</strong> {alert.message}
                  </li>
                ))}
              </ul>
            ) : (
              <p>No major findings flagged.</p>
            )}
          </section>

          <section className="panel-card curriculum-audit-smoke">
            <div className="curriculum-audit-section-header">
              <h3>Live API Smoke Checks</h3>
              <p>
                Executes the same core checks in-browser against <code>/api/performance-tasks</code>{" "}
                and sampled <code>/api/lessons/:id</code> payloads.
              </p>
            </div>

            <div className="audit-smoke-controls">
              <label htmlFor="audit-smoke-size">
                Sample Size
                <input
                  id="audit-smoke-size"
                  type="number"
                  min="1"
                  max="50"
                  value={smokeSampleSize}
                  onChange={(event) => setSmokeSampleSize(event.target.value)}
                />
              </label>
              <label htmlFor="audit-smoke-multi">
                <input
                  id="audit-smoke-multi"
                  type="checkbox"
                  checked={smokeRequireMulti}
                  onChange={(event) => setSmokeRequireMulti(Boolean(event.target.checked))}
                />
                Require at least one multi-standard lesson
              </label>
              <label htmlFor="audit-smoke-data">
                <input
                  id="audit-smoke-data"
                  type="checkbox"
                  checked={smokeRequireData}
                  onChange={(event) => setSmokeRequireData(Boolean(event.target.checked))}
                />
                Require non-empty standards data
              </label>
              <button
                type="button"
                className="primary-btn"
                onClick={runSmoke}
                disabled={smokeRunning}
              >
                {smokeRunning ? "Running..." : "Run Smoke"}
              </button>
            </div>

            {smokeResult?.status === "pass" ? (
              <div className="audit-smoke-result audit-smoke-pass">
                <p>
                  Smoke checks passed. Sampled {smokeResult.summary.sampled} lessons with average{" "}
                  {smokeResult.summary.avgStandardsPerLesson} standards per lesson.
                </p>
                {smokeResult.sampleTask ? (
                  <pre>{JSON.stringify(smokeResult.sampleTask, null, 2)}</pre>
                ) : null}
              </div>
            ) : null}

            {smokeResult?.status === "fail" ? (
              <div className="audit-smoke-result audit-smoke-fail">
                <p>Smoke checks failed: {smokeResult.message}</p>
              </div>
            ) : null}
          </section>

          <section className="panel-card curriculum-audit-lesson-audit">
            <div className="curriculum-audit-section-header">
              <h3>Lesson Audit Pivot</h3>
              <div className="audit-inline-controls">
                <label htmlFor="audit-pivot-mode">
                  Pivot Mode
                  <select
                    id="audit-pivot-mode"
                    value={pivotMode}
                    onChange={(event) => setPivotMode(event.target.value)}
                  >
                    <option value="unit">By Unit</option>
                    <option value="standards-load">By Standards Load</option>
                  </select>
                </label>
              </div>
            </div>

            {pivotMode === "unit" ? (
              <div className="audit-table-wrap">
                <table className="audit-table">
                  <thead>
                    <tr>
                      <th>Unit</th>
                      <th>Lessons</th>
                      <th>Avg Standards</th>
                      <th>Multi-Std Lessons</th>
                      <th>Missing EQ</th>
                      <th>Missing Objectives</th>
                    </tr>
                  </thead>
                  <tbody>
                    {unitPivotRows.map((row) => (
                      <tr key={`unit-${row.unit_number}`}>
                        <td>Unit {row.unit_number}</td>
                        <td>{row.lessons}</td>
                        <td>{row.avg_standards}</td>
                        <td>{row.multi_standard_lessons}</td>
                        <td>{row.lessons_missing_eq}</td>
                        <td>{row.lessons_missing_objectives}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="audit-table-wrap">
                <table className="audit-table">
                  <thead>
                    <tr>
                      <th>Standards Bucket</th>
                      <th>Lesson Count</th>
                    </tr>
                  </thead>
                  <tbody>
                    {standardsLoadPivotRows.map((row) => (
                      <tr key={row.key}>
                        <td>{row.label}</td>
                        <td>{row.count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div className="audit-inline-controls audit-lesson-controls">
              <label htmlFor="audit-lesson-search">
                Lesson Search
                <input
                  id="audit-lesson-search"
                  type="text"
                  value={searchLesson}
                  onChange={(event) => setSearchLesson(event.target.value)}
                  placeholder="Type lesson title..."
                />
              </label>
            </div>

            <div className="audit-table-wrap">
              <table className="audit-table">
                <thead>
                  <tr>
                    <th>Unit</th>
                    <th>Lesson</th>
                    <th>Primary TEKS</th>
                    <th>Standards</th>
                    <th>Essential Question</th>
                    <th>Calendar Days</th>
                    <th>Objective Days</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredLessons.map((row) => (
                    <tr key={row.lesson_id}>
                      <td>{row.unit_number}</td>
                      <td>{row.lesson_title}</td>
                      <td>{row.primary_teks_code || "-"}</td>
                      <td>{row.standards_count}</td>
                      <td>{row.has_essential_question ? "Yes" : "No"}</td>
                      <td>{row.calendar_day_count}</td>
                      <td>{row.objective_day_count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <section className="panel-card curriculum-audit-teks-coverage">
            <div className="curriculum-audit-section-header">
              <h3>TEKS Coverage Detail</h3>
              <label htmlFor="audit-uncovered-only" className="audit-inline-checkbox">
                <input
                  id="audit-uncovered-only"
                  type="checkbox"
                  checked={uncoveredOnly}
                  onChange={(event) => setUncoveredOnly(Boolean(event.target.checked))}
                />
                Show uncovered only
              </label>
            </div>
            <div className="audit-table-wrap">
              <table className="audit-table">
                <thead>
                  <tr>
                    <th>TEKS</th>
                    <th>Category</th>
                    <th>CBA Weight</th>
                    <th>Lesson Count</th>
                    <th>Unit Count</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTeksCoverage.map((row) => (
                    <tr key={row.teks_id}>
                      <td>{row.teks_code}</td>
                      <td>{row.category || "-"}</td>
                      <td>{row.cba_item_weight ?? 0}</td>
                      <td>{row.lesson_count}</td>
                      <td>{row.unit_count}</td>
                      <td>
                        <span className={`audit-status-pill ${row.is_covered ? "audit-status-good" : "audit-status-risk"}`}>
                          {row.is_covered ? "Covered" : "Gap"}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <section className="panel-card curriculum-audit-scope">
            <h3>Scope & Sequence Execution</h3>
            {Array.isArray(report.scopeAudit) && report.scopeAudit.length > 0 ? (
              <div className="audit-table-wrap">
                <table className="audit-table">
                  <thead>
                    <tr>
                      <th>Unit</th>
                      <th>Window</th>
                      <th>Readiness</th>
                      <th>Supporting</th>
                      <th>Processing</th>
                      <th>Lesson Days</th>
                      <th>Lessons</th>
                      <th>Objective Days</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.scopeAudit.map((row) => (
                      <tr key={row.entry_id}>
                        <td>
                          <div>Unit {row.unit_number}</div>
                          <div className="audit-muted">{row.unit_title}</div>
                        </td>
                        <td>
                          {String(row.start_date || "").slice(0, 10)} - {String(row.end_date || "").slice(0, 10)}
                        </td>
                        <td>{row.readiness_count}</td>
                        <td>{row.supporting_count}</td>
                        <td>{row.processing_count}</td>
                        <td>{row.lesson_day_count}</td>
                        <td>{row.lesson_count}</td>
                        <td>{row.objective_day_count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p>No scope sequence entries found for this course.</p>
            )}
          </section>
        </>
      ) : null}
    </div>
  );
};

export default CurriculumAuditPage;
